/**
 * Den Einband eines Projekts gestalten.
 *
 * Zwei Wege, ein Bildschirm:
 *   Presets  – alle vorgefertigten Einbände auf einen Blick, nicht in einer
 *              Ausklappliste. Man sieht sofort, wie das Buch aussehen wird.
 *   Zeichnen – der Einband wird gross, der Pencil geht von selbst an, und
 *              man malt direkt darauf.
 *
 * Wie gezeichnet wird
 *   Es gibt hier KEINE eigene Zeichenlogik. Der Einband meldet sich als
 *   Zeichenfläche beim Rahmen an (features/drawing/surface.ts), genau wie
 *   ein Lead Sheet oder eine Bibel-Fläche. Damit gelten Stift-gegen-Finger,
 *   Radierer, Druckstärke und Rückgängig unverändert – und die Latenz ist
 *   dieselbe wie überall sonst.
 *
 * Wie alles zusammengeht
 *   Drei Ebenen desselben Einbands, von unten nach oben:
 *     1. das Preset (Farbe, Muster, Ruecken)
 *     2. ein eigenes Bild, falls gewaehlt (coverImage)
 *     3. die Zeichnung (coverArtwork)
 *   Die Striche liegen im "drawings"-Store unter "cover:<projektId>". Fürs
 *   Regal wird zusätzlich ein Bild abgelegt (coverArtwork), genau auf den
 *   Einband zugeschnitten. Deshalb kann man jederzeit Preset oder Bild
 *   wechseln, ohne die Zeichnung zu verlieren.
 *
 * Zeichnen auf dem gewaehlten Einband
 *   Beim Zeichnen bleibt der gewaehlte Einband darunter sichtbar. Einzige
 *   Ausnahme: ein neues Projekt, dessen Einband nie bewusst gewaehlt wurde
 *   (coverChosen === false). Dort wird beim ersten Zeichnen weisses Papier
 *   daraus.
 *
 * Zeichenebene
 *   Sie ist auf den Einband begrenzt (clipToContent) und zeichnet nur im
 *   Zeichenmodus. Ausserhalb davon und solange die Einband-Auswahl offen
 *   ist, ist sie ausgeblendet – sonst laegen Striche ueber der Bedienung.
 */
import type { View } from '../app.ts';
import type { Project } from '../types.ts';
import { COVERS, coverAt, drawingCanvasIndex, renderCover, renderProjectCover } from '../ui/cover.ts';
import {
  coverSurfaceId,
  getDrawing,
  getMyCover,
  getProject,
  myCoverSurfaceId,
  saveDrawing as storeDrawing,
  saveMyCover,
  setMyCoverVisibility,
  withMyCover,
} from '../db/repo.ts';
import type { DesignVisibility } from '../db/repo.ts';
import { askConfirm } from '../ui/ask.ts';
import { getActiveSurface, onSurfaceChange } from '../features/drawing/surface.ts';
import { getToolState, setToolState } from '../features/drawing/tools.ts';
import { button, h } from '../ui/dom.ts';
import { icon } from '../ui/icons.ts';
import { toast } from '../ui/toast.ts';

export async function renderCoverEditor(params: Record<string, string>): Promise<View> {
  const existing = await getProject(params.id as string);
  if (!existing) {
    return {
      title: 'Projekt nicht gefunden',
      back: '#/projects',
      body: h('div', { class: 'empty', text: 'Dieses Projekt wurde geloescht.' }),
    };
  }

  /*
   * Der Einband ist persoenlich: angezeigt und bearbeitet wird die eigene
   * Fassung. Gibt es noch keine, startet sie als Kopie des gemeinsamen
   * Einbands – samt Strichen, damit eine vorhandene Zeichnung
   * weiterbearbeitet werden kann.
   */
  const mine = await getMyCover(existing.id);
  const surfaceId = await myCoverSurfaceId(existing.id);
  if (!mine) {
    const shared = await getDrawing(coverSurfaceId(existing.id));
    if (shared.strokes.length > 0 && (await getDrawing(surfaceId)).strokes.length === 0) {
      await storeDrawing({ ...shared, id: surfaceId });
    }
  }
  let project: Project = withMyCover(existing, mine);

  /** Aenderung am eigenen Einband speichern; das Projekt selbst bleibt, wie es ist. */
  async function saveCover(patch: Partial<Project>): Promise<Project> {
    const next = { ...project, ...patch };
    try {
      await saveMyCover(next.id, next);
    } catch (err) {
      console.error(err);
      toast('Einband zu gross zum Speichern – ein kleineres Bild wählen', 3500);
      return project;
    }
    return next;
  }
  /** Werkzeugzustand von vorher, damit er beim Verlassen zurückkommt. */
  const toolBefore = getToolState();
  let drawing = false;

  /* ---------------------------------------------------- Der Einband */

  /** Der Einband mit allen Ebenen. Beim Zeichnen ohne die gespeicherte Zeichnung. */
  const coverEl = renderCover(project.cover);
  coverEl.classList.add('cover-edit__cover');
  const stage = h('div', { class: 'cover-edit__stage' }, coverEl);

  /**
   * Baut den Einband neu auf, OHNE etwas zu speichern.
   * Beim Zeichnen fehlt die gespeicherte Zeichnung: dann malt die echte
   * Zeichenebene an derselben Stelle, beides zusammen waere doppelt.
   */
  function paintCover(): void {
    const next = renderProjectCover({
      ...project,
      coverMode: drawing ? 'preset' : project.coverMode,
    });
    coverEl.setAttribute('style', next.getAttribute('style') ?? '');
    coverEl.className = next.className + ' cover-edit__cover';
    coverEl.replaceChildren(...Array.from(next.childNodes));
  }
  paintCover();

  /** Zeichenebene nur zeigen, wenn wirklich gezeichnet wird. */
  function syncLayer(): void {
    getActiveSurface()?.setVisible(drawing && presetSheet.hidden);
  }

  /* ----------------------------------------------------- Die Presets */

  const presetSheet = h('div', {
    class: 'cover-presets',
    attrs: { hidden: 'hidden', role: 'dialog', 'aria-label': 'Einband wählen' },
  });

  /**
   * ALLE Presets auf einmal, als echte Vorschauen.
   * Bewusst keine Ausklappliste: man soll sehen, wofür man sich entscheidet.
   */
  const presetGrid = h('div', { class: 'cover-presets__grid' });
  const presetButtons: HTMLElement[] = [];

  /* Eigenes Bild: erste Kachel, damit man es nicht suchen muss. */
  const fileInput = h('input', {
    attrs: { type: 'file', accept: 'image/*', hidden: 'hidden' },
    on: { change: () => void onImagePicked() },
  });
  const imagePreview = h('span', { class: 'cover-presets__preview cover-presets__upload' });
  const imageBtn = h(
    'button',
    {
      class: 'cover-presets__item',
      attrs: { type: 'button', title: 'Eigenes Bild', 'aria-label': 'Eigenes Bild wählen' },
      on: { click: () => fileInput.click() },
    },
    imagePreview,
    h('span', { class: 'cover-presets__name', text: 'Eigenes Bild' }),
  );
  presetGrid.append(imageBtn, fileInput);

  function paintImageTile(): void {
    imagePreview.replaceChildren();
    if (project.coverImage) {
      imagePreview.append(h('img', { attrs: { src: project.coverImage, alt: '' } }));
    } else {
      imagePreview.append(icon('image'), h('span', { text: 'Foto wählen' }));
    }
    const on = Boolean(project.coverImage);
    imageBtn.classList.toggle('cover-presets__item--on', on);
    imageBtn.setAttribute('aria-pressed', String(on));
  }
  paintImageTile();

  async function onImagePicked(): Promise<void> {
    const file = fileInput.files?.[0];
    fileInput.value = '';
    if (!file) return;
    try {
      const dataUrl = await coverImageFrom(file);
      project = await saveCover({ coverImage: dataUrl, coverChosen: true });
      paintCover();
      markPreset();
      toast('Eigenes Bild gesetzt');
    } catch (err) {
      console.error(err);
      toast('Dieses Bild lässt sich nicht öffnen', 3000);
    }
  }

  COVERS.forEach((def, index) => {
    const preview = renderCover(index);
    preview.classList.add('cover-presets__preview');
    const el = h(
      'button',
      {
        class: 'cover-presets__item',
        attrs: { type: 'button', title: def.name, 'aria-label': def.name },
        on: { click: () => void choosePreset(index) },
      },
      preview,
      h('span', { class: 'cover-presets__name', text: def.name }),
    );
    presetButtons.push(el);
  });
  // Buchleinen zuerst zeigen, danach die uebrigen. presetButtons bleibt in
  // der Reihenfolge von COVERS, weil der Index das gespeicherte Merkmal ist.
  const order = COVERS.map((def, index) => ({ def, index })).sort(
    (a, b) => Number(Boolean(b.def.texture)) - Number(Boolean(a.def.texture)),
  );
  for (const { index } of order) presetGrid.append(presetButtons[index] as HTMLElement);

  function markPreset(): void {
    paintImageTile();
    presetButtons.forEach((el, index) => {
      const on = index === project.cover && !project.coverImage;
      el.classList.toggle('cover-presets__item--on', on);
      el.setAttribute('aria-pressed', String(on));
    });
  }
  markPreset();

  presetSheet.append(
    h(
      'header',
      { class: 'cover-presets__head' },
      h('h2', { class: 'cover-presets__title', text: 'Einband' }),
      button('Fertig', () => togglePresets(false), { class: 'btn btn--small' }),
    ),
    presetGrid,
  );

  function togglePresets(open: boolean): void {
    presetSheet.hidden = !open;
    body.classList.toggle('cover-edit--sheet', open);
    syncLayer();
  }

  /**
   * Preset wählen: sofort sichtbar, sofort gespeichert.
   * Die Zeichnung bleibt darüber liegen, ein eigenes Bild wird entfernt.
   */
  async function choosePreset(index: number): Promise<void> {
    project = await saveCover({ cover: index, coverImage: undefined, coverChosen: true });
    paintCover();
    markPreset();
    toast(coverAt(index).name);
  }

  /* ----------------------------------------------------- Zeichenmodus */

  const drawHint = h('p', {
    class: 'cover-edit__hint',
    text: 'Mit dem Apple Pencil direkt auf den Einband zeichnen. Der Finger bedient weiter die App.',
  });

  /**
   * Hinein und hinaus – bewusst ruhiger als eine normale Umschaltung.
   * Der Einband wächst, die Bedienung tritt zurück, der Stift geht an.
   */
  function setDrawing(on: boolean): void {
    drawing = on;
    body.classList.toggle('cover-edit--drawing', on);

    /*
     * Beim ersten Zeichnen auf einem nie gewaehlten Einband: weisses Papier.
     * Sonst bleibt der gewaehlte Einband (Preset oder Bild) darunter.
     * Erst im Speicher, dann nebenher in der Datenbank: ein schnelles
     * Aus/An soll nicht auf einen Schreibvorgang warten.
     */
    if (on && project.coverChosen === false) {
      void saveCover({ cover: drawingCanvasIndex(), coverChosen: true });
      project = { ...project, cover: drawingCanvasIndex(), coverChosen: true };
    }

    togglePresets(false);
    paintCover();
    markPreset();

    // Der Stift soll sofort schreiben können, ohne dass man erst ein
    // Werkzeug suchen muss.
    if (on) setToolState({ active: true, tool: 'pen' });
    else setToolState({ active: false });

    syncLayer();
    syncButtons();
    // Die Zeichenebene muss nach der Grössenänderung neu messen. Die
    // Animation dauert, also am Ende noch einmal.
    getActiveSurface()?.refresh();
    setTimeout(() => getActiveSurface()?.refresh(), 460);
  }

  const drawBtn = button('Zeichnen', () => setDrawing(!drawing), { class: 'btn btn--primary' });
  const undoBtn = button('Rückgängig', () => getActiveSurface()?.undo(), { class: 'btn' });
  const redoBtn = button('Wiederholen', () => getActiveSurface()?.redo(), { class: 'btn' });
  const clearBtn = button(
    'Leeren',
    () => {
      if (!confirm('Die Zeichnung auf diesem Einband löschen?')) return;
      getActiveSurface()?.clearAll();
    },
    { class: 'btn' },
  );
  const saveBtn = button('Sichern', () => void saveDrawing(), { class: 'btn btn--primary' });

  function syncButtons(): void {
    const surface = getActiveSurface();
    drawBtn.textContent = drawing ? 'Zeichnen beenden' : 'Zeichnen';
    for (const el of [undoBtn, redoBtn, clearBtn, saveBtn]) el.hidden = !drawing;
    drawHint.hidden = !drawing;
    undoBtn.toggleAttribute('disabled', !surface?.canUndo);
    redoBtn.toggleAttribute('disabled', !surface?.canRedo);
    clearBtn.toggleAttribute('disabled', (surface?.strokeCount ?? 0) === 0);
  }

  // Beim Zeichnen wechseln Rückgängig und Wiederholen ihren Zustand. Hier
  // meldet sich auch die Zeichenebene an, sobald sie steht.
  const stopWatch = onSurfaceChange(() => {
    syncLayer();
    syncButtons();
    void renewOldArtwork();
  });

  /**
   * Aeltere Zeichnungen wurden mitsamt dem Bereich um den Einband als Bild
   * gesichert und sahen im Regal verzerrt und doppelt aus. Einmal aus den
   * gespeicherten Strichen neu erzeugen – ohne dass jemand etwas tun muss.
   */
  let renewing = false;
  async function renewOldArtwork(): Promise<void> {
    const surface = getActiveSurface();
    if (renewing || !surface || drawing) return;
    if (project.coverMode !== 'custom' || project.coverArtworkVersion === 2) return;
    if (surface.strokeCount === 0) return;
    renewing = true;
    const png = surface.snapshot();
    if (png !== '') {
      project = await saveCover({ coverArtwork: png, coverArtworkVersion: 2 });
      paintCover();
    }
  }

  /**
   * Die Zeichnung als Bild fürs Regal ablegen.
   * Die Striche selbst liegen ohnehin schon in der Datenbank – dieses Bild
   * ist nur die Abkürzung für die Anzeige.
   */
  async function saveDrawing(): Promise<void> {
    const surface = getActiveSurface();
    if (!surface) return;
    if (surface.strokeCount === 0) {
      // Nichts gezeichnet: dann bleibt es beim Preset, statt ein leeres
      // Bild zu speichern, das das Buch weiss aussehen liesse.
      project = await saveCover({ coverMode: 'preset', coverArtwork: undefined });
      paintCover();
      toast('Kein Strich – Einband bleibt beim Preset');
      setDrawing(false);
      markPreset();
      return;
    }
    const png = surface.snapshot();
    if (png === '') {
      toast('Die Zeichnung konnte nicht gesichert werden');
      return;
    }
    project = await saveCover({ coverMode: 'custom', coverArtwork: png, coverArtworkVersion: 2, coverChosen: true });
    toast('Einband gesichert');
    setDrawing(false);
    markPreset();
  }

  /* ------------------------------------------------ Privat / Öffentlich */

  /**
   * Wer sieht diesen Einband? Privat: nur ich. Oeffentlich: alle in der
   * Band (er wird zum gemeinsamen Einband des Projekts). Umschalten mit
   * Rueckfrage, weil es die Ansicht der anderen aendert.
   */
  let coverVisibility: DesignVisibility = mine?.visibility ?? 'private';
  const visSwitch = h('div', { class: 'segmented cover-edit__vis', attrs: { role: 'group', 'aria-label': 'Wer sieht den Einband' } });
  const visButtons = (['private', 'public'] as DesignVisibility[]).map((value) => {
    const el = h('button', {
      class: 'segmented__item',
      text: value === 'private' ? 'Privat' : 'Öffentlich',
      attrs: { type: 'button', title: value === 'private' ? 'Nur ich sehe diesen Einband' : 'Alle in der Band sehen diesen Einband' },
      on: { click: () => void switchVisibility(value) },
    });
    visSwitch.append(el);
    return { value, el };
  });
  function markVisibility(): void {
    for (const { value, el } of visButtons) {
      el.classList.toggle('segmented__item--on', value === coverVisibility);
      el.setAttribute('aria-pressed', String(value === coverVisibility));
    }
  }
  markVisibility();

  async function switchVisibility(next: DesignVisibility): Promise<void> {
    if (next === coverVisibility) return;
    const ok = await askConfirm({
      title: next === 'public' ? 'Einband veröffentlichen?' : 'Einband auf privat stellen?',
      text:
        next === 'public'
          ? 'Bist du dir sicher? Alle in der Band sehen dann deinen Einband für dieses Projekt – auch jede weitere Änderung daran.'
          : 'Bist du dir sicher? Dann siehst nur du deinen Einband. Die anderen sehen wieder den gemeinsamen Einband von vorher.',
      confirm: next === 'public' ? 'Veröffentlichen' : 'Auf privat stellen',
    });
    if (!ok) return;
    try {
      await setMyCoverVisibility(project, project, next);
      coverVisibility = next;
      markVisibility();
      toast(next === 'public' ? 'Einband ist jetzt öffentlich' : 'Einband ist jetzt privat');
    } catch (err) {
      console.error(err);
      toast('Nicht gespeichert – bitte erneut versuchen', 3000);
    }
  }

  const bar = h(
    'div',
    { class: 'cover-edit__bar' },
    visSwitch,
    button('Presets', () => togglePresets(presetSheet.hidden), { class: 'btn' }),
    drawBtn,
    undoBtn,
    redoBtn,
    clearBtn,
    saveBtn,
  );

  const body = h('div', { class: 'cover-edit' }, stage, drawHint, bar, presetSheet);
  syncButtons();

  return {
    title: project.name || 'Ohne Namen',
    subtitle: 'Einband',
    back: '#/projects',
    flush: true,
    body,
    // Der Einband IST die Zeichenfläche. Als Einheit die Höhe des Einbands
    // durch zehn: dadurch bleibt ein Strich an seiner Stelle, egal wie gross
    // der Einband gerade dargestellt wird.
    drawing: {
      id: surfaceId,
      host: stage,
      content: coverEl,
      getUnit: () => Math.max(8, coverEl.getBoundingClientRect().height / 10),
      clipToContent: true,
      canDraw: () => drawing && presetSheet.hidden,
    },
    cleanup: () => {
      stopWatch();
      // Den Werkzeugzustand von vorher zurückgeben: wer ohne Stift
      // hereinkam, soll ohne Stift herausgehen.
      setToolState({ active: toolBefore.active, tool: toolBefore.tool });
      document.body.classList.remove('cover-edit--drawing', 'cover-edit--sheet');
    },
  };
}

/**
 * Ein Foto als Einband: auf 3:4 zugeschnitten (Mitte), hoechstens 900 x 1200
 * Pixel, als JPEG. So bleibt es klein genug fuer Geraet und Cloud und sieht
 * im Regal trotzdem scharf aus.
 */
async function coverImageFrom(file: File): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
    await img.decode();
    const W = 900;
    const H = 1200;
    const scale = Math.max(W / img.naturalWidth, H / img.naturalHeight);
    const sw = W / scale;
    const sh = H / scale;
    const sx = (img.naturalWidth - sw) / 2;
    const sy = (img.naturalHeight - sh) / 2;
    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Kein Zeichenkontext');
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, W, H);
    return canvas.toDataURL('image/jpeg', 0.85);
  } finally {
    URL.revokeObjectURL(url);
  }
}
